import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

/* ─── Contact cards data ───────────────────────────────────────── */
const contactCards = [
    {
        icon: "/assets/img/bg/contact-icon01.png",
        active: false,
        links: [
            { href: "tel:+919986255343", label: "+91 9986255343 (Careers)" },
            { href: "tel:+919513107575", label: "+91 9513107575 (Admissions)" },
        ],
    },
    {
        icon: "/assets/img/bg/contact-icon02.png",
        active: true,
        links: [
            { href: "mailto:principal.akaship@yahoo.com", label: "principal.akaship@yahoo.com" },
            { href: "mailto:akashpharma023@gmail.com",    label: "akashpharma023@gmail.com"    },
        ],
    },
    {
        icon: "/assets/img/bg/contact-icon03.png",
        active: false,
        links: [
            {
                href: "https://maps.app.goo.gl/dX7oRaQyfrqHSZ55A",
                label: "Akash Institute of Pharmacy, Prasannahalli Main Road, Devanahalli, Near International Airport, Bengaluru, Karnataka 562110",
                target: "_blank",
            },
        ],
    },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const titleVariants = {
    hidden: { opacity: 0, y: -40 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
    },
};

const cardContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 55, scale: 0.95 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

const linkStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.35 } },
};

const linkItem = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

/* ─── Single Contact Card ──────────────────────────────────────── */
function ContactCard({ card }) {
    const cardRef   = useRef(null);
    const iconRef   = useRef(null);
    const glowRef   = useRef(null);
    const { ref: inViewRef, inView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: icon spring entrance */
    useEffect(() => {
        if (inView && iconRef.current) {
            gsap.fromTo(
                iconRef.current,
                { scale: 0.5, opacity: 0, rotation: -15 },
                { scale: 1, opacity: 1, rotation: 0, duration: 0.7, ease: "back.out(2)", delay: 0.25 }
            );
        }
    }, [inView]);

    /* GSAP: hover lift + glow */
    const handleEnter = () => {
        gsap.to(cardRef.current,  { y: -10, duration: 0.3, ease: "power2.out" });
        gsap.to(glowRef.current,  { opacity: 1, duration: 0.3 });
        gsap.to(iconRef.current,  { scale: 1.1, rotation: 5, duration: 0.35, ease: "power2.out" });
    };
    const handleLeave = () => {
        gsap.to(cardRef.current,  { y: 0,  duration: 0.3, ease: "power2.out" });
        gsap.to(glowRef.current,  { opacity: 0, duration: 0.3 });
        gsap.to(iconRef.current,  { scale: 1, rotation: 0, duration: 0.35, ease: "power2.out" });
    };

    const setRefs = (el) => {
        cardRef.current = el;
        inViewRef(el);
    };

    return (
        <motion.div
            className="col-xl-4 col-md-12"
            variants={cardVariants}
        >
            <div
                ref={setRefs}
                className={`services-box text-center${card.active ? " active" : ""}`}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                style={{ position: "relative", overflow: "hidden" }}
            >
                {/* Glow overlay on hover */}
                <div
                    ref={glowRef}
                    style={{
                        position: "absolute", inset: 0,
                        background: "radial-gradient(ellipse at center, rgba(30,90,168,0.08) 0%, transparent 70%)",
                        opacity: 0, pointerEvents: "none",
                    }}
                />

                {/* Icon */}
                <div className="services-icon">
                    <img
                        ref={iconRef}
                        src={card.icon}
                        alt="icon"
                        style={{ opacity: 0 }}
                    />
                </div>

                {/* Links — staggered */}
                <motion.div
                    className="services-content2"
                    variants={linkStagger}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                >
                    {card.links.map((link, i) => (
                        <motion.h5 key={i} variants={linkItem}>
                            <a
                                href={link.href}
                                target={link.target || undefined}
                                rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                            >
                                {link.label}
                            </a>
                        </motion.h5>
                    ))}
                </motion.div>
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Touch() {
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: gridRef,  inView: gridInView  } = useInView({ threshold: 0.2, triggerOnce: true });

    return (
        <section id="services" className="services-area pt-120 pb-90 fix">
            <div className="container">

                {/* ── Section Title ── */}
                <div className="row">
                    <div className="col-lg-12">
                        <motion.div
                            ref={titleRef}
                            className="section-title text-center mb-50"
                            variants={titleVariants}
                            initial="hidden"
                            animate={titleInView ? "visible" : "hidden"}
                        >
                            <h5>
                                <i className="fal fa-graduation-cap" /> Contact Info
                            </h5>
                            <h2>Get In Touch</h2>
                        </motion.div>
                    </div>
                </div>

                {/* ── Contact Cards ── */}
                <motion.div
                    ref={gridRef}
                    className="row"
                    variants={cardContainerVariants}
                    initial="hidden"
                    animate={gridInView ? "visible" : "hidden"}
                >
                    {contactCards.map((card, i) => (
                        <ContactCard key={i} card={card} />
                    ))}
                </motion.div>

            </div>
        </section>
    );
}

export default Touch;
